import { IdcardOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { Form, InputNumber, Radio, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import { type IdConfiguration } from "../../redux/settings.types.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

export type LeadProjectIdFormData = Pick<IdConfiguration, "mode" | "start_from">

interface LeadProjectIdCardProps {
  form: FormInstance<LeadProjectIdFormData>
  locked?: boolean
}

function SectionLabel({
  text,
  hint
}: {
  text: string
  hint: string
}) {
  return (
    <span className="flex flex-col">
      <Typography.Text className="font-semibold text-[15px]">
        {text}
      </Typography.Text>
      <Typography.Text type="secondary" className="font-normal">
        {hint}
      </Typography.Text>
    </span>
  )
}

export function LeadProjectIdCard({
  form,
  locked = false
}: LeadProjectIdCardProps) {
  const { t } = useTranslation()
  const mode = Form.useWatch("mode", form)

  const methods: {
    value: LeadProjectIdFormData["mode"]
    label: string
    description: string
  }[] = [
    {
      value: "incremental",
      label: t("settingsConfiguration.methods.incremental.label"),
      description: t("settingsConfiguration.methods.incremental.description")
    },
    {
      value: "random",
      label: t("settingsConfiguration.methods.random.label"),
      description: t("settingsConfiguration.methods.random.description")
    }
  ]

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<IdcardOutlined className="text-blue-600" />}
      title={t("settingsConfiguration.title")}
      subtitle={t("settingsConfiguration.subtitle")}
    >
      <Form.Item
        name="mode"
        required
        label={
          <SectionLabel
            text={t("settingsConfiguration.numberingMethod.label")}
            hint={t("settingsConfiguration.numberingMethod.hint")}
          />
        }
        rules={[{ required: true }]}
      >
        <Radio.Group className="w-full">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {methods.map((method) => {
              const selected = mode === method.value

              return (
                <label
                  key={method.value}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-4 transition-colors cursor-pointer",
                    selected
                      ? "border-primary bg-primary-bg"
                      : "border-[#E5E7EB] bg-white hover:border-gray-300"
                  )}
                >
                  <Radio
                    value={method.value}
                    aria-label={method.label}
                    className="mt-0.5"
                  />
                  <span className="flex flex-col">
                    <Typography.Text className="font-semibold text-[15px]">
                      {method.label}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {method.description}
                    </Typography.Text>
                  </span>
                </label>
              )
            })}
          </div>
        </Radio.Group>
      </Form.Item>

      {mode === "incremental" && (
        <Form.Item
          name="start_from"
          required
          label={
            <SectionLabel
              text={t("settingsConfiguration.startFrom.label")}
              hint={t("settingsConfiguration.startFrom.hint")}
            />
          }
          rules={[
            {
              required: true,
              message: t("settingsConfiguration.startFrom.required")
            },
            {
              type: "integer",
              min: 1,
              max: 900000,
              message: t("settingsConfiguration.startFrom.range")
            }
          ]}
          extra={
            <span className="mt-1 flex items-center gap-1.5">
              <InfoCircleOutlined className="text-gray-400" />
              <Typography.Text type="secondary">
                {locked
                  ? t("settingsConfiguration.startFrom.locked")
                  : t("settingsConfiguration.startFrom.unlocked")}
              </Typography.Text>
            </span>
          }
        >
          <InputNumber<number>
            min={1}
            max={900000}
            precision={0}
            controls={false}
            disabled={locked}
            aria-label={t("settingsConfiguration.startFrom.label")}
            className="w-full max-w-[280px]"
            // Only pad on blur — reformatting every keystroke while the user
            // is mid-edit fights the cursor.
            formatter={(value, info) => {
              if (value === undefined || value === null) return ""
              return info.userTyping ? info.input : String(value).padStart(6, "0")
            }}
            // A cleared field must parse to nothing, not silently become 1 —
            // otherwise the required rule can never see it as empty.
            parser={(value) => {
              const digits = (value ?? "").replace(/\D/g, "")
              return digits ? Number(digits) : (null as unknown as number)
            }}
          />
        </Form.Item>
      )}
    </SettingsCard>
  )
}
